import { z } from 'zod';
import { Clip, Effect, Exportbereich, ID, Marke, SequenceSettings, Subtitles, Track, Transition } from './timeline.js';
import { bereichAnheften, bereicheNachziehen } from './bereiche.js';
import { ueberlappungHerstellen } from './uebergang.js';
/**
 * Collaborative editing.
 *
 * Every change to a timeline is expressed as one of the operations below. The
 * server owns the sequence: it applies operations in the order they arrive,
 * bumps a revision counter and fans the operation out to the other clients.
 * Clients apply their own operations optimistically and re-apply whatever the
 * server echoes back.
 *
 * Operations are written so that applying the same set in any order converges:
 * property writes are last-writer-wins on a single field, adds and removes are
 * keyed by clip id and are idempotent. That is weaker than a full CRDT (two
 * people dragging the *same* clip still fight over it) but it is enough for the
 * real pattern -- people work on different parts of the same cut -- and it costs
 * a fraction of the complexity.
 */
const ClipPatch = Clip.partial().omit({ id: true });
export const Op = z.discriminatedUnion('op', [
    z.object({ op: z.literal('addClip'), trackId: ID, clip: Clip }),
    z.object({ op: z.literal('removeClip'), clipId: ID }),
    z.object({ op: z.literal('updateClip'), clipId: ID, patch: ClipPatch }),
    z.object({ op: z.literal('moveClip'), clipId: ID, toTrackId: ID, start: z.number().min(0) }),
    z.object({ op: z.literal('setClipEffects'), clipId: ID, effects: z.array(Effect).max(8) }),
    z.object({ op: z.literal('setTransition'), clipId: ID, transition: Transition.nullable() }),
    /** `index` puts the track at a position -- a new video track belongs under
     *  the other video tracks, not after the audio; omitted, it goes last. */
    z.object({ op: z.literal('addTrack'), track: Track, index: z.number().int().min(0).optional() }),
    z.object({ op: z.literal('removeTrack'), trackId: ID }),
    z.object({ op: z.literal('updateTrack'), trackId: ID, patch: Track.partial().omit({ id: true, clips: true }) }),
    z.object({ op: z.literal('updateSettings'), patch: SequenceSettings.partial() }),
    z.object({ op: z.literal('updateSubtitles'), patch: Subtitles.partial() }),
    z.object({ op: z.literal('setBrandKit'), brandKitId: ID.nullable() }),
    /** Benannte Stellen (S5). Fein statt „die ganze Liste“: zwei Leute, die an
     *  verschiedenen Stellen Marken setzen, sollen sich nicht gegenseitig
     *  überschreiben -- dieselbe Überlegung wie bei den Clips. */
    z.object({ op: z.literal('addMarke'), marke: Marke }),
    z.object({ op: z.literal('updateMarke'), markeId: ID, patch: Marke.partial().omit({ id: true }) }),
    z.object({ op: z.literal('removeMarke'), markeId: ID }),
    /* Exportbereiche (S33) -- wie die Marken: je Bereich ein Schritt, damit zwei
       Leute nicht die Liste des jeweils anderen überschreiben. */
    z.object({ op: z.literal('addExportbereich'), bereich: Exportbereich }),
    z.object({ op: z.literal('updateExportbereich'), bereichId: ID, patch: Exportbereich.partial().omit({ id: true }) }),
    z.object({ op: z.literal('removeExportbereich'), bereichId: ID }),
]);
function cloneTimeline(t) {
    return structuredClone(t);
}
/**
 * Apply one operation. Returns a new timeline; the input is never mutated.
 * Unknown ids are ignored rather than thrown, so a late-arriving operation for
 * a clip somebody else deleted is a no-op instead of a desync.
 */
export function applyOp(timeline, op) {
    const t = wendeAn(timeline, op);
    // Angeheftete Bereiche folgen ihren Bildern (7.166). Nur wenn es welche
    // gibt -- sonst bleibt alles, wie es war, bis aufs Objekt.
    if (t !== timeline && t.exportbereiche.some((b) => b.angeheftet)) {
        switch (op.op) {
            case 'addClip':
            case 'removeClip':
            case 'updateClip':
            case 'moveClip':
            case 'setTransition':
            case 'removeTrack':
                bereicheNachziehen(t);
                break;
            default:
                break;
        }
    }
    return t;
}
function wendeAn(timeline, op) {
    const t = cloneTimeline(timeline);
    switch (op.op) {
        case 'addClip': {
            const track = t.tracks.find((tr) => tr.id === op.trackId);
            if (!track)
                return timeline;
            if (track.clips.some((c) => c.id === op.clip.id))
                return timeline;
            track.clips.push(op.clip);
            track.clips.sort((a, b) => a.start - b.start);
            return t;
        }
        case 'removeClip': {
            for (const track of t.tracks) {
                const i = track.clips.findIndex((c) => c.id === op.clipId);
                if (i >= 0) {
                    track.clips.splice(i, 1);
                    return t;
                }
            }
            return timeline;
        }
        case 'updateClip': {
            for (const track of t.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip) {
                    Object.assign(clip, op.patch);
                    track.clips.sort((a, b) => a.start - b.start);
                    return t;
                }
            }
            return timeline;
        }
        case 'moveClip': {
            let moved;
            for (const track of t.tracks) {
                const i = track.clips.findIndex((c) => c.id === op.clipId);
                if (i >= 0) {
                    moved = track.clips.splice(i, 1)[0];
                    break;
                }
            }
            if (!moved)
                return timeline;
            const target = t.tracks.find((tr) => tr.id === op.toTrackId);
            if (!target)
                return timeline;
            moved.start = op.start;
            target.clips.push(moved);
            target.clips.sort((a, b) => a.start - b.start);
            return t;
        }
        case 'setClipEffects': {
            for (const track of t.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip) {
                    clip.effects = op.effects;
                    return t;
                }
            }
            return timeline;
        }
        case 'setTransition': {
            for (const track of t.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip) {
                    clip.transitionIn = op.transition;
                    // Der Übergang ist die Überlappung (7.89): der Clip rückt so weit
                    // nach links, dass er um seine Länge in den vorigen hineinragt, und
                    // alles dahinter auf derselben Spur rückt mit. Ohne das erfindet
                    // der Export die Überlappung im Bild und lässt den Ton stehen.
                    for (const { clipId, start } of ueberlappungHerstellen(track.clips, op.clipId, op.transition?.duration ?? 0)) {
                        const anderer = track.clips.find((c) => c.id === clipId);
                        if (anderer)
                            anderer.start = start;
                    }
                    return t;
                }
            }
            return timeline;
        }
        case 'addTrack': {
            if (t.tracks.some((tr) => tr.id === op.track.id))
                return timeline;
            if (op.index !== undefined && op.index < t.tracks.length)
                t.tracks.splice(op.index, 0, op.track);
            else
                t.tracks.push(op.track);
            return t;
        }
        case 'removeTrack': {
            const i = t.tracks.findIndex((tr) => tr.id === op.trackId);
            if (i < 0)
                return timeline;
            t.tracks.splice(i, 1);
            return t;
        }
        case 'updateTrack': {
            const track = t.tracks.find((tr) => tr.id === op.trackId);
            if (!track)
                return timeline;
            Object.assign(track, op.patch);
            return t;
        }
        case 'updateSettings':
            Object.assign(t.settings, op.patch);
            return t;
        case 'updateSubtitles':
            Object.assign(t.subtitles, op.patch);
            return t;
        case 'setBrandKit':
            t.brandKitId = op.brandKitId;
            return t;
        // Marken (S5). Zweimal dieselbe Marke anzulegen ändert nichts, und eine
        // zu entfernen, die nicht da ist, auch nicht -- so wie bei den Clips.
        case 'addMarke':
            if (!t.marken.some((m) => m.id === op.marke.id))
                t.marken.push(structuredClone(op.marke));
            t.marken.sort((a, b) => a.at - b.at);
            return t;
        case 'updateMarke': {
            const marke = t.marken.find((m) => m.id === op.markeId);
            if (marke)
                Object.assign(marke, op.patch);
            t.marken.sort((a, b) => a.at - b.at);
            return t;
        }
        case 'removeMarke': {
            const i = t.marken.findIndex((m) => m.id === op.markeId);
            if (i >= 0)
                t.marken.splice(i, 1);
            return t;
        }
        // Exportbereiche (S33), nach demselben Muster -- und nach dem Anfang
        // sortiert, damit die Liste im Blatt dieselbe Reihenfolge hat wie die
        // Zeitleiste.
        case 'addExportbereich':
            if (!t.exportbereiche.some((b) => b.id === op.bereich.id)) {
                const neu = structuredClone(op.bereich);
                // Angeheftet angelegt, aber noch ohne Anker: an die Bilder, die jetzt
                // an seinen Kanten stehen (7.166). Ein zurückgeholter Bereich bringt
                // seine Anker mit und behält sie.
                if (neu.angeheftet && !neu.vonAnker && !neu.bisAnker)
                    bereichAnheften(t, neu);
                t.exportbereiche.push(neu);
            }
            t.exportbereiche.sort((a, b) => a.von - b.von);
            return t;
        case 'updateExportbereich': {
            const bereich = t.exportbereiche.find((b) => b.id === op.bereichId);
            if (bereich) {
                Object.assign(bereich, op.patch);
                // Kante gezogen, angeheftet oder gelöst: neu an die Bilder heften,
                // die jetzt an den Kanten stehen (7.166). Name und Farbe ändern
                // daran nichts.
                // Ein anderes Element gewählt (7.196): ebenso.
                if ('von' in op.patch || 'bis' in op.patch || 'angeheftet' in op.patch || 'heftenAn' in op.patch) {
                    bereichAnheften(t, bereich);
                }
            }
            t.exportbereiche.sort((a, b) => a.von - b.von);
            return t;
        }
        case 'removeExportbereich': {
            const i = t.exportbereiche.findIndex((b) => b.id === op.bereichId);
            if (i >= 0)
                t.exportbereiche.splice(i, 1);
            return t;
        }
    }
}
export function applyOps(timeline, ops) {
    return ops.reduce(applyOp, timeline);
}
/** Messages a client may send over the project websocket. */
export const ClientMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hello'), sinceRev: z.number().int().min(0) }),
    z.object({
        type: z.literal('ops'),
        ops: z.array(Op).min(1).max(200),
        clientSeq: z.number().int(),
        /**
         * Wer schickt (7.101). Zusammen mit `clientSeq` erkennt der Server ein
         * Paket wieder, das er schon angewendet hat -- und wendet es nicht ein
         * zweites Mal an.
         *
         * Der Fall, um den es geht: das Paket kommt an, der Server rechnet es ein,
         * und die Bestätigung geht auf dem Rückweg verloren (die Leitung fällt).
         * Dann steht es beim nächsten Verbinden noch in der Warteschlange und geht
         * ein zweites Mal hinaus. Hat jemand inzwischen rückgängig gemacht, steht
         * danach wieder da, was er zurückgenommen hat.
         *
         * Optional, weil ältere Fassungen und die Prüfstände ohne auskommen: ohne
         * Kennung gibt es keine Wiedererkennung, und das ist genau der Stand von
         * vorher.
         */
        klient: z.string().min(1).max(64).optional(),
    }),
    z.object({
        type: z.literal('presence'),
        playhead: z.number().min(0),
        selection: z.array(ID).max(50).default([]),
    }),
    z.object({ type: z.literal('ping') }),
]);
/** Deterministic colour per user so peer cursors stay stable across sessions. */
/**
 * Die Farben, unter denen man sich aussuchen kann.
 *
 * Dieselben sieben, die `peerColor` von jeher verteilt hat, plus zwei --
 * damit eine gewählte Farbe neben einer gerechneten nicht auffällt wie ein
 * Fremdkörper und damit bei neun Leuten noch etwas übrig ist.
 */
export const FARBEN = [
    '#f43f5e',
    '#22c55e',
    '#3b82f6',
    '#f59e0b',
    '#a855f7',
    '#06b6d4',
    '#ec4899',
    '#84cc16',
    '#f97316',
];
/** Ob eine Farbe eine ist, die wir anbieten. Was sonst kommt, gilt nicht. */
export function istFarbe(wert) {
    return FARBEN.includes(wert);
}
/**
 * Die Farbe eines Menschen: die gewählte, sonst die gerechnete.
 *
 * Eine Stelle, weil sie an vielen gebraucht wird -- Kürzel, Abspielkopf,
 * eigener Avatar. Zwei Rechnungen davon laufen irgendwann auseinander, und
 * dann trägt derselbe Mensch an zwei Orten zwei Farben.
 */
export function farbeVon(userId, gewaehlt) {
    if (gewaehlt && istFarbe(gewaehlt))
        return gewaehlt;
    // Ohne Kennung die erste Farbe statt eines Absturzes: eine Farbe zu
    // bestimmen ist nichts, wofür eine Seite stehenbleiben darf.
    return userId ? peerColor(userId) : FARBEN[0];
}
export function peerColor(userId) {
    let h = 0;
    for (let i = 0; i < userId.length; i++)
        h = (h * 31 + userId.charCodeAt(i)) >>> 0;
    const palette = ['#f43f5e', '#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#06b6d4', '#ec4899'];
    return palette[h % palette.length];
}
/**
 * The operation that undoes `op`, computed against the timeline as it was
 * *before* `op` was applied. Undo is expressed as ordinary operations rather
 * than by restoring a snapshot, so undoing does not quietly throw away what a
 * colleague changed in the meantime.
 *
 * Returns null when the operation cannot be undone from the state given -- an
 * add of a clip that was already there, or a change to something that is gone.
 */
export function invertOp(before, op) {
    switch (op.op) {
        case 'addClip':
            return { op: 'removeClip', clipId: op.clip.id };
        case 'removeClip': {
            for (const track of before.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip)
                    return { op: 'addClip', trackId: track.id, clip: structuredClone(clip) };
            }
            return null;
        }
        case 'updateClip': {
            for (const track of before.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (!clip)
                    continue;
                const patch = {};
                for (const key of Object.keys(op.patch)) {
                    patch[key] = structuredClone(clip[key]);
                }
                return { op: 'updateClip', clipId: op.clipId, patch: patch };
            }
            return null;
        }
        case 'moveClip': {
            for (const track of before.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip)
                    return { op: 'moveClip', clipId: op.clipId, toTrackId: track.id, start: clip.start };
            }
            return null;
        }
        case 'setClipEffects': {
            for (const track of before.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip)
                    return { op: 'setClipEffects', clipId: op.clipId, effects: structuredClone(clip.effects) };
            }
            return null;
        }
        case 'setTransition': {
            for (const track of before.tracks) {
                const clip = track.clips.find((c) => c.id === op.clipId);
                if (clip)
                    return { op: 'setTransition', clipId: op.clipId, transition: structuredClone(clip.transitionIn) };
            }
            return null;
        }
        case 'addTrack':
            return { op: 'removeTrack', trackId: op.track.id };
        case 'removeTrack': {
            const index = before.tracks.findIndex((t) => t.id === op.trackId);
            const track = before.tracks[index];
            // Back where it was, or undo would put a video track under the audio.
            return track ? { op: 'addTrack', track: structuredClone(track), index } : null;
        }
        case 'updateTrack': {
            const track = before.tracks.find((t) => t.id === op.trackId);
            if (!track)
                return null;
            const patch = {};
            for (const key of Object.keys(op.patch)) {
                patch[key] = structuredClone(track[key]);
            }
            return { op: 'updateTrack', trackId: op.trackId, patch: patch };
        }
        case 'updateSettings': {
            const patch = {};
            for (const key of Object.keys(op.patch)) {
                patch[key] = before.settings[key];
            }
            // `undefined` überlebt JSON nicht: „nie gesetzt“ heisst beim Magneten aus (7.201).
            if ('magnetisch' in patch && patch.magnetisch === undefined)
                patch.magnetisch = false;
            return { op: 'updateSettings', patch: patch };
        }
        case 'updateSubtitles': {
            const patch = {};
            for (const key of Object.keys(op.patch)) {
                patch[key] = structuredClone(before.subtitles[key]);
            }
            return { op: 'updateSubtitles', patch: patch };
        }
        case 'setBrandKit':
            return { op: 'setBrandKit', brandKitId: before.brandKitId };
        case 'addMarke':
            return { op: 'removeMarke', markeId: op.marke.id };
        case 'updateMarke': {
            const marke = before.marken.find((m) => m.id === op.markeId);
            if (!marke)
                return null;
            const patch = {};
            for (const key of Object.keys(op.patch)) {
                patch[key] = structuredClone(marke[key]);
            }
            return { op: 'updateMarke', markeId: op.markeId, patch: patch };
        }
        case 'removeMarke': {
            const marke = before.marken.find((m) => m.id === op.markeId);
            return marke ? { op: 'addMarke', marke: structuredClone(marke) } : null;
        }
        case 'addExportbereich':
            return { op: 'removeExportbereich', bereichId: op.bereich.id };
        case 'updateExportbereich': {
            const bereich = before.exportbereiche.find((b) => b.id === op.bereichId);
            if (!bereich)
                return null;
            const patch = {};
            for (const key of Object.keys(op.patch)) {
                patch[key] = structuredClone(bereich[key]);
            }
            // `undefined` überlebt den Weg zum Server nicht (JSON lässt den
            // Schlüssel weg), und dann täte Rückgängig dort nichts (7.166).
            if ('angeheftet' in patch && patch.angeheftet === undefined)
                patch.angeheftet = false;
            // Dasselbe für Art und Element (7.196): `null` ist „keine Art gesetzt“,
            // ein leeres Objekt „an der untersten Bildspur“.
            if ('art' in patch && patch.art === undefined)
                patch.art = null;
            if ('heftenAn' in patch && patch.heftenAn === undefined)
                patch.heftenAn = {};
            return { op: 'updateExportbereich', bereichId: op.bereichId, patch: patch };
        }
        case 'removeExportbereich': {
            const bereich = before.exportbereiche.find((b) => b.id === op.bereichId);
            return bereich ? { op: 'addExportbereich', bereich: structuredClone(bereich) } : null;
        }
    }
}
/**
 * What a change was, in words, so the undo button can say what it will take
 * back instead of only offering an arrow. A batch that does one kind of thing
 * -- three clips deleted -- is named after that thing; a mixed batch is not
 * worth a sentence and says so.
 *
 * The wording describes the *original* action. Undo stores the label together
 * with the operations that reverse it, so "Rückgängig: Clip verschoben" reads
 * the same before and after.
 */
export function describeOp(op) {
    switch (op.op) {
        case 'addClip':
            return 'Clip eingefügt';
        case 'removeClip':
            return 'Clip gelöscht';
        case 'moveClip':
            return 'Clip verschoben';
        case 'updateClip':
            return describeClipPatch(op.patch);
        case 'setClipEffects':
            return 'Effekte geändert';
        case 'setTransition':
            return op.transition ? 'Übergang gesetzt' : 'Übergang entfernt';
        case 'addTrack':
            return 'Spur hinzugefügt';
        case 'removeTrack':
            return 'Spur gelöscht';
        case 'updateTrack':
            return 'Spur geändert';
        case 'updateSettings':
            return 'Einstellungen geändert';
        case 'updateSubtitles':
            return 'Untertitel geändert';
        case 'setBrandKit':
            return op.brandKitId ? 'Senderpaket gewählt' : 'Senderpaket entfernt';
        case 'addMarke':
            return 'Marke gesetzt';
        case 'updateMarke':
            return 'at' in op.patch ? 'Marke verschoben' : 'Marke benannt';
        case 'removeMarke':
            return 'Marke entfernt';
        case 'addExportbereich':
            return 'Exportbereich angelegt';
        case 'updateExportbereich':
            return 'name' in op.patch ? 'Exportbereich benannt' : 'Exportbereich geändert';
        case 'removeExportbereich':
            return 'Exportbereich entfernt';
    }
}
/**
 * One patch can be a trim, a volume change or a move on the canvas. Naming the
 * field that changed is the difference between "Clip geändert" -- which tells
 * nobody anything -- and "Lautstärke geändert".
 */
function describeClipPatch(patch) {
    const keys = Object.keys(patch);
    const has = (...names) => names.some((name) => keys.includes(name));
    if (has('start', 'duration', 'sourceIn'))
        return 'Clip zugeschnitten';
    if (has('gainKeyframes'))
        return 'Pegelverlauf geändert';
    if (has('gain', 'muted'))
        return 'Lautstärke geändert';
    if (has('transform', 'keyframes'))
        return 'Bild bewegt';
    if (has('effects'))
        return 'Effekte geändert';
    if (has('source'))
        return 'Text geändert';
    if (has('fadeIn', 'fadeOut'))
        return 'Blende geändert';
    if (has('speed'))
        return 'Tempo geändert';
    if (has('transitionIn'))
        return 'Übergang geändert';
    if (has('animation'))
        return 'Bewegung geändert';
    if (has('label'))
        return 'Clip benannt';
    return 'Clip geändert';
}
export function describeOps(ops) {
    if (ops.length === 0)
        return 'Änderung';
    const labels = ops.map(describeOp);
    const first = labels[0];
    return labels.every((label) => label === first) ? first : 'Mehrere Änderungen';
}
//# sourceMappingURL=ops.js.map